/**
 * Identity hints for boot-critical org/stek resolution (stale-while-revalidate).
 *
 * The app's org/stek context is resolved from URL *names*, but every API call
 * needs *identities* in headers — so the organizations and steks list fetches
 * gate the entire page-load request waterfall. Caching lets resolution (and
 * therefore the page's data burst) start immediately; the real fetches still
 * run, and the selection managers fall through to a full re-selection when a
 * hinted identity is absent from the fresh list, so staleness self-heals.
 *
 * Privacy: localStorage outlives sign-out and is readable by anyone with
 * access to the browser profile, so no names or entity metadata are stored —
 * only bare identity nanoids, keyed by a SHA-256 hash of
 * (user scope | kind | name). The name is the lookup INPUT (it comes from the
 * URL), never recoverable from the cache. `clearAllCached()` additionally
 * wipes everything on sign-out.
 */

const PREFIX = "mystekz.swr.v2";

async function hintKey(
    scope: string,
    kind: string,
    name: string
): Promise<string> {
    const preimage = new TextEncoder().encode(`${scope}|${kind}|${name}`);
    const digest = await crypto.subtle.digest("SHA-256", preimage);
    const hex = Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    return `${PREFIX}.${hex}`;
}

/** Look up the cached identity for a (user, kind, name) triple. */
export const loadIdentityHint = async (
    scope: string | undefined,
    kind: string,
    name: string | undefined
): Promise<string | undefined> => {
    if (!scope || !name) {
        return undefined;
    }
    try {
        return (
            localStorage.getItem(await hintKey(scope, kind, name)) ?? undefined
        );
    } catch {
        // Private mode / quota / no SubtleCrypto — behave as a cache miss.
        return undefined;
    }
};

/** Remember the identity for a (user, kind, name) triple. Best-effort. */
export const saveIdentityHint = async (
    scope: string | undefined,
    kind: string,
    name: string | undefined,
    identity: string
): Promise<void> => {
    if (!scope || !name) {
        return;
    }
    try {
        localStorage.setItem(await hintKey(scope, kind, name), identity);
    } catch {
        // Best-effort cache — ignore storage failures.
    }
};

/** Drop every cached hint (called on sign-out). */
export const clearAllCached = (): void => {
    try {
        for (const key of Object.keys(localStorage)) {
            if (key.startsWith("mystekz.swr.")) {
                localStorage.removeItem(key);
            }
        }
    } catch {
        // Best-effort.
    }
};
